import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import React from 'react';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { PackWithBuyButton } from '#src/components/commonCard/BuyPacks/PackWithBuyButton/index.tsx';
import { PremiumPackTitle } from '#src/components/commonCard/BuyPacks/PremiumPack/PremiumPackTitle/index.tsx';
import { usePackAssets } from '#src/components/commonCard/PacksTypePage/usePackAssets.ts';

import type { HighlightedPack_cardPack } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  padding: var(--double-unit);
  width: 100%;
  background:
    linear-gradient(to bottom, transparent 0%, var(--c-black) 100%),
    var(--background-image) bottom -10% center/cover no-repeat;
  border-radius: var(--double-unit);
  --border-gradient: linear-gradient(
    to bottom,
    var(--league-color) 0%,
    transparent 20%
  );
  ${borderGradientStyle};
  overflow: hidden;
  justify-content: space-between;
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  top: -1px;
  height: var(--double-unit);
  width: 200%;
  transform: rotate(180deg);
`;

type Props = {
  premiumCardPack: Nullable<HighlightedPack_cardPack>;
  cardPackGroupId: string;
};

export const HighlightedPack = ({
  premiumCardPack,
  cardPackGroupId,
}: Props) => {
  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroupId),
  });

  return (
    <Root
      style={
        {
          '--league-color': premiumCardPack?.primaryColor,
          '--background-image': `url(${bgImage})`,
        } as CSSProperties
      }
    >
      <StyledNeonEdge
        color={premiumCardPack?.primaryColor ?? ''}
        showHalo="always"
        showNeon="always"
      />
      {!!premiumCardPack && <PremiumPackTitle cardPack={premiumCardPack} />}
      {!!premiumCardPack && (
        <PackWithBuyButton
          cardPack={premiumCardPack}
          cardPackGroupId={cardPackGroupId}
        />
      )}
    </Root>
  );
};

HighlightedPack.fragments = {
  cardPack: gql`
    fragment HighlightedPack_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      ...PackWithBuyButton_cardPack
      ...PremiumPackTitle_cardPack
    }
    ${PackWithBuyButton.fragments.cardPack}
    ${PremiumPackTitle.fragments.cardPack}
  ` as TypedDocumentNode<HighlightedPack_cardPack>,
};
